/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * GatewayV2Socket — Erlang :9100 gateway için TAM protokol adaptörü.
 *
 * FE'nin mevcut ws-client.ts'i düz {type,data} zarfı + custom auth/presence:join/
 * ping mesajları kullanır ve :9100'e OLDUĞU GİBİ bağlanamaz — çünkü :9100 tam
 * Discord Gateway zarfını konuşur: {op,t,d,s} + HELLO(op10)/heartbeat(op1)/
 * ack(op11)/IDENTIFY(op2)/RESUME(op6)/RECONNECT(op7)/INVALID_SESSION(op9).
 *
 * Bu sınıf O protokol katmanıdır. kaynak-referansın GatewaySocket.ts'i (~1180 satır)
 * referans alındı; yaşam-döngüsü artık TAM PORT: HELLO/IDENTIFY/RESUME/
 * RECONNECT/INVALID_SESSION/heartbeat-ack-timeout/hello-timeout/resume-timeout/
 * resume-window/exponential-backoff+jitter/close-code-güdümlü session temizliği
 * hepsi kaynak-referans 1-1. Outbound op-yüzeyi de tam: op3/4/8/14/15/16 (op15/16 bu
 * turda eklendi). Yalnız callback-tabanlı (EventEmitter değil) — TEBHOS'un
 * bu adaptör için bilinçli mimari tercihi, davranış paritesini etkilemiyor.
 *
 * COMPRESSION: kaynak-referans 1-1. `compress=zstd-stream&stream=1` negotiate edilir;
 * bağlantı ömrü boyunca TEK süregelen zstd akışı (ZSTD_e_flush, ASLA
 * ZSTD_e_end — bkz. tebhos_gateway/_build/.../ezstd/src/ezstd.erl
 * compress_streaming/2) hem gönderirken hem alırken sürdürülür. Bunun için
 * kaynak-referansın `libfluxcore` Rust→WASM crate'i `rust/libtebhoscore` olarak
 * portlandı (bkz. Cargo.toml başlığı — yalnız zstd-stream yüzeyi, görsel
 * katman bu turun kapsamı dışı) ve GatewayCompression.ts (kaynak-referans
 * 1-1 portu) ile buraya bağlandı. WASM decoder/
 * encoder handle'ları bu socket örneğinin ömrü boyunca TEK örnek olarak
 * tutulur; decode hatasında kaynak-referans `handleRecoverableCompressionDecodeError`
 * paritesiyle `compress=none`'a düşülüp resumable reconnect tetiklenir (geri
 * dönüş YOK — kaynak-referansta da yok).
 *
 * GATEWAY-CUTOVER (2026-07-16): Bu sınıf artık ws-client.ts'in TEK ve KOŞULSUZ
 * transport adaptörü — GatewayV2Flag.ts (opt-in flag) SİLİNDİ. Eski :9000
 * (tokio, düz {type,data}) yolu FE'den tamamen söküldü; backend tarafında da
 * tokio ws-gateway KALINTISIZ SİLİNDİ (GATEWAY-CUTOVER 2026-07-17) — :9000
 * artık hiçbir şey dinlemiyor, tek gateway Erlang :9100.
 *
 * OUTBOUND op-eşleme (kaynak-referans GatewaySocket.ts 1-1): FE'nin giden aksiyonları
 * Discord op'larına eşlenir. Discord modelinde "oda" kavramı yoktur — guild'ler
 * READY'de otomatik-scope edilir, üyeler op14 lazy_request / op8
 * request_guild_members ile, voice op4 voice_state_update ile, presence op3 ile
 * istenir; guild/kanal sayımları op15/op16 ile; typing ise gateway'de DEĞİL
 * REST'te (POST /channels/{id}/typing). Bu sınıf o op-üreten public API'yi
 * sağlar; ws-client.send() flag-aktifken giden {type,...} zarflarını bu
 * metodlara yönlendirir, typing'i REST'e alır.
 *
 * op5 (VOICE_SERVER_PING): kaynak-referansta da TANIMLI ama HİÇBİR YERDE
 * gönderilmiyor/işlenmiyor (constants.erl'de var, gateway_handler_dispatch.erl
 * içinde karşılığı yok — grep doğrulandı). Bu yüzden burada da yalnız Op
 * sabitinde parite için durur; gönderim/alım kasıtlı olarak YOK (uydurma
 * davranış üretmemek için).
 *
 * KAPSAM-DIŞI (kaynak-referansta da gateway-op'u OLMAYAN, REST-güdümlü aksiyonlar):
 * reaction:update / pin:update / broadcast:message / dm:reaction-update /
 * invite:send / dm:call-end — bunlar Discord modelinde REST ile yapılır ve
 * sunucu ilgili DISPATCH'i REST sonrası fan-out eder; gateway'de op karşılığı
 * yoktur → ws-client'ta no-op düşerler (uydurma op üretilmez).
 */

import {
  type CompressionType,
  GatewayCompression,
  getPreferredCompression,
  isGatewayCompressionError,
} from "@app/features/gateway/utils/GatewayCompression";
// WS-CHURN FIX (2026-08-11): TÜM bağlantı yaşam-döngüsü zamanlayıcıları
// (heartbeat tick/ack + hello/resume/invalid-session/reconnect) Web Worker
// tabanlı zamanlayıcıya taşındı. Ana-thread setTimeout arka plan sekmesinde
// dakikada bire kadar kısılıyor → 33sn'lik heartbeat 60sn+'ye kayıyor →
// Erlang gateway 45sn sessizlikte 4009 session_timeout ile kapatıyordu
// (prod: 35dk'da 61 reconnect, 0 RESUME). Worker timer'ları bu kısıtlamadan
// muaf; ayrıntı ve fallback davranışı için WorkerTimers.ts başlığına bak.
import { workerTimers } from "@app/features/gateway/utils/WorkerTimers";

/** Discord gateway op-code'ları (kaynak-referans GatewayConstants ile birebir, op5/15/16 dahil). */
const Op = {
  DISPATCH: 0,
  HEARTBEAT: 1,
  IDENTIFY: 2,
  PRESENCE_UPDATE: 3,
  VOICE_STATE_UPDATE: 4,
  /** Tanımlı ama kaynak-referansta da kullanılmıyor — bkz. dosya başlığı. */
  VOICE_SERVER_PING: 5,
  RESUME: 6,
  RECONNECT: 7,
  REQUEST_GUILD_MEMBERS: 8,
  INVALID_SESSION: 9,
  HELLO: 10,
  HEARTBEAT_ACK: 11,
  GATEWAY_ERROR: 12,
  LAZY_REQUEST: 14,
  REQUEST_GUILD_COUNTS: 15,
  REQUEST_CHANNEL_MEMBER_COUNTS: 16,
} as const;

/**
 * IDENTIFY d.flags bit'leri (kaynak-referans GatewayIdentifyFlags 1-1). Erlang
 * session_manager_shard_drain:should_debounce_reactions bu bit'e bakar:
 * açıksa oturum MESSAGE_REACTION_ADD'leri 650ms biriktirip >1 girişte tek
 * MESSAGE_REACTION_ADD_MANY yayar (session_dispatch_voice.erl).
 */
const IdentifyFlags = {
  DEBOUNCE_MESSAGE_REACTIONS: 1 << 1,
} as const;

/** WS close code'ları (kaynak-referans GatewayCloseCodes 1-1, constants.erl close_code_to_num ile eşleşir). */
const CloseCode = {
  UNKNOWN_ERROR: 4000,
  UNKNOWN_OPCODE: 4001,
  DECODE_ERROR: 4002,
  NOT_AUTHENTICATED: 4003,
  AUTHENTICATION_FAILED: 4004,
  ALREADY_AUTHENTICATED: 4005,
  INVALID_SEQ: 4007,
  RATE_LIMITED: 4008,
  SESSION_TIMEOUT: 4009,
  INVALID_SHARD: 4010,
  SHARDING_REQUIRED: 4011,
  INVALID_API_VERSION: 4012,
} as const;

/** kaynak-referans GATEWAY_TIMEOUTS 1-1 (GatewaySocket.ts satır 28-35). */
const TIMEOUTS = {
  HeartbeatAck: 15000,
  ResumeResponse: 15000,
  ResumeWindow: 180000,
  MinReconnect: 1000,
  MaxReconnect: 60000,
  Hello: 20000,
} as const;

interface WirePayload {
  op: number;
  d?: any;
  s?: number;
  t?: string;
}

/** op4 voice_state_update d-payload (kaynak-referans GatewayVoiceStateUpdateParams alt-kümesi). */
export interface GatewayV2VoiceStateParams {
  guild_id: string | null;
  channel_id: string | null;
  self_mute: boolean;
  self_deaf: boolean;
  // DM-SESLİ-ARAMA saf-1-1 dalgası (2026-07-27) — kaynak-referans MediaEngine voice-state
  // payload'ının opsiyonel alanları (verilirse wire'a aynen taşınır; verilmezse
  // eski minimal set korunur — geriye-dönük additive).
  self_video?: boolean;
  self_stream?: boolean;
  viewer_stream_keys?: ReadonlyArray<string>;
  connection_id?: string | null;
}

export interface GatewayV2Callbacks {
  /** Her DISPATCH (op0) için: çevrilmiş event adı + data. */
  onDispatch: (eventName: string, data: any) => void;
  /** İlk READY geldiğinde (bağlantı tam kurulunca). */
  onReady?: (data: any) => void;
  /** Socket açıldı (transport seviyesi; henüz READY değil). */
  onOpen?: () => void;
  /** Socket kapandı (her sebeple). */
  onClose?: () => void;
  /** op12 GATEWAY_ERROR (kaynak-referans gatewayError event paritesi). */
  onGatewayError?: (error: { code?: string; message?: string }) => void;
  /**
   * Kalıcı kimlik doğrulama hatası (close 4004, kaynak-referans handleAuthFailure
   * paritesi). Sonrasında OTOMATİK reconnect DENENMEZ — çağıran taraf
   * (ws-client) logout/re-login akışını üstlenmeli.
   */
  onAuthFailure?: () => void;
}

export interface GatewayV2Options {
  url: string;
  token: string;
  profileId: string;
  profileName: string;
  /** presence:join eşdeğeri — IDENTIFY presence'ı için başlangıç status'u. */
  initialStatus: string;
}

/**
 * kaynak-referans GatewayState 1-1 (GatewaySocket.ts:36-41). probeAfterResume
 * makinesi bu duruma göre dallanır — TEBHOS'ta önceden yoktu (uyku/arka plan
 * dönüşünde zombi soket ancak heartbeat-ack zaman aşımıyla, ~48sn sonra fark
 * ediliyordu; kaynak-referans visibilitychange/online/pageshow anında probe eder).
 */
const GatewayState = {
  Disconnected: "DISCONNECTED",
  Connecting: "CONNECTING",
  Connected: "CONNECTED",
  Reconnecting: "RECONNECTING",
} as const;
type GatewayStateT = (typeof GatewayState)[keyof typeof GatewayState];

interface GatewayResumeProbeOptions {
  accelerateReconnect?: boolean;
}

/**
 * kaynak-referans ExponentialBackoff (RetryScheduler.ts) 1-1 alt-kümesi: factor=2,
 * jitter=±%25, min=1000ms, max=60000ms, sınırsız deneme (kaynak-referansta da
 * maxNumOfAttempts default Infinity — TEBHOS'un eski 10-deneme tavanı
 * KALDIRILDI: kaynak-referans asla pes etmez, yalnız backoff'la yavaşlar).
 * getCurrentAttempts kaynak-referans loglama paritesi için eklendi.
 */
class ReconnectBackoff {
  private attempts = 0;

  next(): number {
    this.attempts++;
    const base = Math.min(TIMEOUTS.MinReconnect * 2 ** (this.attempts - 1), TIMEOUTS.MaxReconnect);
    const maxJitter = base * 0.25;
    const jitter = (Math.random() * 2 - 1) * maxJitter;
    return Math.max(TIMEOUTS.MinReconnect, Math.min(base + jitter, TIMEOUTS.MaxReconnect));
  }

  getCurrentAttempts(): number {
    return this.attempts;
  }

  reset(): void {
    this.attempts = 0;
  }
}

export class GatewayV2Socket {
  private ws: WebSocket | null = null;

  // ── Compression (kaynak-referans GatewaySocket.ts compression bloğu 1-1) ──
  /** Bu bağlantı-oturumu boyunca sabitlenen negotiate tercihi; decode hatasında "none"a düşer ve GERİ DÖNMEZ. */
  private compression: CompressionType = getPreferredCompression();
  private payloadDecompressor: GatewayCompression | null = null;
  private compressionFallbackInProgress = false;

  // ── Heartbeat ──
  // Zamanlayıcı id'leri workerTimers'a ait (WS-CHURN FIX) — global
  // clearTimeout'a ASLA verilmez, yalnız workerTimers.clearTimeout'a.
  private heartbeatTimeoutId: number | null = null;
  private heartbeatAckTimeoutId: number | null = null;
  private heartbeatIntervalMs = 0;
  private awaitingAck = false;
  private lastHeartbeatSentAt: number | null = null;
  private lastHeartbeatAckAt: number | null = null;

  // ── Oturum / sequence ──
  // kaynak-referans paritesi: lastSequenceNumber 0'dan başlar (null DEĞİL) —
  // heartbeat d'si her zaman sayı, canResumeSession seq'e bakmaz.
  private lastSeq = 0;
  private sessionId: string | null = null;
  private lastGatewayMessageAt: number | null = null;
  private isReady = false;

  // ── Yaşam-döngüsü ──
  private closedByUser = false;
  private shouldReconnectImmediately = false;
  private readonly backoff = new ReconnectBackoff();
  // Bunlar da workerTimers id'si (arka planda kısılmadan çalışmaları ŞART:
  // reconnect zinciri gizli sekmede de ilerlemeli ki 60sn'lik gateway resume
  // TTL'i dolmadan RESUME denenebilsin).
  private reconnectTimer: number | null = null;
  private helloTimeoutId: number | null = null;
  private resumeTimeoutId: number | null = null;
  private invalidSessionTimeoutId: number | null = null;
  // kaynak-referans connectionState/connectStartedAt/lastReconnectAt 1-1 —
  // probeAfterResume + nextReconnectDelay bu üçüne dayanır.
  private connectionState: GatewayStateT = GatewayState.Disconnected;
  private connectStartedAt = 0;
  private lastReconnectAt = 0;

  constructor(
    private readonly opts: GatewayV2Options,
    private readonly cb: GatewayV2Callbacks
  ) {}

  connect(): void {
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }
    // kaynak-referans connect() 1-1: zaten Connecting/Connected ise no-op; değilse
    // Connecting'e geç ve bağlantı-başlangıç zamanını işaretle (stuck-CONNECTING
    // tespiti probeAfterResume'da buna bakar).
    if (this.connectionState === GatewayState.Connecting || this.connectionState === GatewayState.Connected) {
      // ws yoksa ama state takılı kaldıysa düşmeye devam et (savunma) — kaynak-referansta
      // openSocket teardown'u bu durumu üretmez; TEBHOS'ta da üretmemeli.
      if (this.ws) return;
    }
    this.closedByUser = false;
    this.connectStartedAt = Date.now();
    this.connectionState = GatewayState.Connecting;
    // Eski (varsa çoktan kapanmış) bağlantının decoder/encoder handle'ları
    // yeni bağlantıya SIZMASIN — kaynak-referans teardownSocket 1-1: her yeni WS
    // bağlantısı kendi streaming DCtx/CCtx'ini sıfırdan açar.
    if (this.payloadDecompressor) {
      this.payloadDecompressor.destroy();
      this.payloadDecompressor = null;
    }
    try {
      // Discord-spec query: encoding=json, compress=<this.compression>.
      // zstd-stream seçiliyse stream=1 eklenir (Erlang gateway_compress:
      // parse_compression/1 bunu ayırt eder). "none" fallback'i decode
      // hatasında sabitlenir (handleRecoverableCompressionDecodeError) —
      // Erlang'ın bilinmeyen/eksik değer → none davranışıyla da örtüşür.
      const sep = this.opts.url.includes("?") ? "&" : "?";
      let url = `${this.opts.url}${sep}v=1&encoding=json&compress=${this.compression}`;
      if (this.compression === "zstd-stream") url += "&stream=1";
      this.ws = new WebSocket(url);
      if (this.compression !== "none") {
        this.ws.binaryType = "arraybuffer";
        this.payloadDecompressor = new GatewayCompression(this.compression, true);
        void this.payloadDecompressor.warmup();
      } else {
        this.ws.binaryType = "blob";
      }
      this.compressionFallbackInProgress = false;
      this.startHelloTimeout();

      // kaynak-referans isCurrentSocketEvent paritesi: connect() hızlı art arda
      // çağrılırsa (ör. forceReconnect + doğal onclose çakışması) ESKİ
      // socket'in geç gelen event'i YENİ this.ws'i etkilemesin diye her
      // handler `event.target === this.ws` doğrular. Eski TEBHOS'ta bu
      // korumasızdı — stale-event durumunda yeni bağlantının session'ını
      // yanlışlıkla temizleyebilirdi.
      this.ws.onopen = (event) => {
        if (event.target !== this.ws) return;
        console.log("[WS9100] transport open (HELLO bekleniyor)");
        this.cb.onOpen?.();
      };
      this.ws.onmessage = (event) => {
        if (event.target !== this.ws) return;
        void this.handleSocketMessage(event);
      };
      this.ws.onclose = (event) => {
        if (event.target !== this.ws) return;
        this.handleClose(event.code, event.reason);
      };
      this.ws.onerror = (event) => {
        if (event.target !== this.ws) return;
        /* onclose zaten reconnect tetikler */
      };
    } catch (e) {
      console.warn("[WS9100] connect error:", e);
      this.clearHelloTimeout();
      if (!this.closedByUser) {
        this.connectionState = GatewayState.Reconnecting;
        this.scheduleReconnect();
      } else {
        this.connectionState = GatewayState.Disconnected;
      }
    }
  }

  /**
   * WS mesaj alım köprüsü — kaynak-referans handleSocketMessage 1-1: binary frame'i
   * decompress kuyruğuna besler, mid-flush boş chunk'ı sessizce atlar (henüz
   * tam bir JSON birikmedi demektir), decode hatasını recoverable-fallback
   * yoluna yönlendirir.
   */
  private async handleSocketMessage(event: MessageEvent): Promise<void> {
    try {
      const json = await this.extractPayload(event);
      if (event.target !== this.ws) return; // await sırasında socket değişmiş olabilir
      if (json == null) return;
      this.handleFrame(json);
    } catch (e) {
      const error = e instanceof Error ? e : new Error(String(e));
      if (this.handleRecoverableCompressionDecodeError(error)) return;
      console.error("[WS9100] fatal decode error:", error);
      this.triggerReconnect(CloseCode.DECODE_ERROR, "Fatal message decode error");
    }
  }

  /** kaynak-referans extractPayload 1-1: binary→decompress, blob→text, string→olduğu gibi. */
  private async extractPayload(event: MessageEvent): Promise<string | null> {
    const data: unknown = event.data;
    if (data instanceof ArrayBuffer) {
      if (!this.payloadDecompressor) {
        throw new Error("binary veri geldi ama decompressor yapılandırılmadı");
      }
      const chunk = await this.payloadDecompressor.decompress(data);
      if (!chunk) return null; // flush sınırı henüz gelmedi — ek chunk bekleniyor
      return chunk;
    }
    if (typeof data === "string") return data;
    if (data instanceof Blob) return await data.text();
    throw new Error(`desteklenmeyen gateway payload tipi: ${typeof data}`);
  }

  /**
   * kaynak-referans handleRecoverableCompressionDecodeError 1-1: WASM decode hatasında
   * "none"a düşer + immediate resumable reconnect tetikler. Bir kez düşünce
   * GERİ DÖNMEZ (kaynak-referansta da yok) — bozuk/desteklenmeyen ortamda kalıcı
   * güvenli mod.
   */
  private handleRecoverableCompressionDecodeError(error: Error): boolean {
    if (!isGatewayCompressionError(error)) return false;
    if (this.compressionFallbackInProgress) {
      console.log("[WS9100] compression olmadan yeniden bağlanılırken sıkıştırılmış mesaj yok sayılıyor");
      return true;
    }
    if (this.compression === "none") return false;
    this.compressionFallbackInProgress = true;
    this.compression = "none";
    this.shouldReconnectImmediately = true;
    console.warn("[WS9100] gateway compression decode hatası; compression olmadan yeniden bağlanılıyor:", error);
    this.triggerReconnect(CloseCode.DECODE_ERROR, "Retrying without compression");
    return true;
  }

  disconnect(): void {
    this.closedByUser = true;
    this.isReady = false;
    this.connectionState = GatewayState.Disconnected;
    this.clearAllTimers();
    this.stopHeartbeat();
    if (this.payloadDecompressor) {
      this.payloadDecompressor.destroy();
      this.payloadDecompressor = null;
    }
    const hadSocket = this.ws != null;
    if (this.ws) {
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CLOSING) {
        this.ws.close(1000, "client disconnect");
      }
      // this.ws'i HEMEN null'luyoruz (stale-event guard'ı bu socket'in geç
      // gelecek close event'ini artık yok sayar) — bu yüzden onClose'u burada
      // ELLE tetikliyoruz, handleClose'un tetiklemesini beklemiyoruz.
      this.ws = null;
    }
    // Session temizle — sonraki connect sıfırdan IDENTIFY eder.
    this.clearSession();
    this.backoff.reset();
    if (hadSocket) this.cb.onClose?.();
  }

  get connected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  // ── Uyku/ağ-dönüşü probe makinesi (kaynak-referans GatewaySocket.ts 1-1; eski
  // TEBHOS'ta TAMAMEN YOKTU). Sekme arka plandayken tarayıcı timer'ları
  // kısıtlar/dondurur → heartbeat gecikir → sunucu 45sn'de 4009
  // session_timeout ile kapatır (4009 session'ı da temizler → her dönüşte
  // sıfırdan IDENTIFY). Bu makine görünürlük/ağ dönüşünde bağlantıyı ANINDA
  // yoklar: bayatsa beklemeden koparıp (resume-window içinde) RESUME'la döner,
  // sağlamsa erken heartbeat atar. ──────────────────────────────────────────

  /** kaynak-referans handleNetworkStatusChange 1-1 (window 'online' kablosu ws-client'ta). */
  handleNetworkStatusChange(online: boolean): void {
    console.log(`[WS9100] network status: ${online ? "online" : "offline"}`);
    if (!online) return;
    this.probeAfterResume("online", { accelerateReconnect: true });
  }

  /** kaynak-referans probeAfterResume 1-1 — visibilitychange/pageshow/online sonrası çağrılır. */
  probeAfterResume(reason: string, options: GatewayResumeProbeOptions = {}): void {
    console.log(`[WS9100] probeAfterResume: reason=${reason}, state=${this.connectionState}`);
    switch (this.connectionState) {
      case GatewayState.Connected:
        this.verifyConnectionFreshness(reason);
        return;
      case GatewayState.Reconnecting:
      case GatewayState.Disconnected: {
        if (this.connectionState === GatewayState.Disconnected && this.closedByUser) {
          return; // kullanıcı-kaynaklı kapanış — resume'da yeniden bağlanma
        }
        this.reconnectAfterResume(reason, options.accelerateReconnect === true);
        return;
      }
      case GatewayState.Connecting: {
        const connectingFor = this.connectStartedAt > 0 ? Date.now() - this.connectStartedAt : 0;
        if (connectingFor > TIMEOUTS.Hello) {
          console.warn(`[WS9100] ${connectingFor}ms'dir CONNECTING'de takılı (reason=${reason}) → zorla taze deneme`);
          this.forceReconnect(`Stuck connecting on resume: ${reason}`);
        }
        return;
      }
    }
  }

  /** kaynak-referans forceReconnectFromResume 1-1 (pageshow bfcache yolu). */
  forceReconnectFromResume(reason: string): void {
    console.warn(`[WS9100] resume-handler zorla reconnect istedi: ${reason}`);
    this.forceReconnect(reason);
  }

  /** kaynak-referans reconnectAfterResume 1-1. */
  private reconnectAfterResume(reason: string, accelerateReconnect: boolean): void {
    if (accelerateReconnect) {
      if (this.reconnectTimer != null) {
        workerTimers.clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }
      this.shouldReconnectImmediately = true;
      this.backoff.reset();
      this.connect();
      return;
    }
    if (this.reconnectTimer != null) {
      return; // zaten planlı — backoff korunur (kaynak-referans aynı)
    }
    console.log(`[WS9100] resume sonrası backoff'lu reconnect planlanıyor: reason=${reason}`);
    this.connectionState = GatewayState.Reconnecting;
    this.scheduleReconnect({ allowImmediate: false });
  }

  /** kaynak-referans verifyConnectionFreshness 1-1: bayat/uyumsuz soketi hemen ele. */
  private verifyConnectionFreshness(reason: string): void {
    if (!this.heartbeatIntervalMs) return;
    const now = Date.now();
    const lastSeenAt = this.lastGatewayActivityAt();
    if (lastSeenAt == null) return;
    const staleness = now - lastSeenAt;
    const staleThreshold = Math.max(Math.floor(this.heartbeatIntervalMs * 1.5), 30_000);
    if (staleness > staleThreshold) {
      console.warn(
        `[WS9100] resume'da bayat gateway: son aktiviteden bu yana ${staleness}ms (eşik ${staleThreshold}ms, reason=${reason})`
      );
      this.forceReconnect(`Stale connection on resume: ${reason}`);
      return;
    }
    if (this.ws?.readyState !== WebSocket.OPEN) {
      console.warn(
        `[WS9100] durum Connected ama socket readyState=${this.ws?.readyState ?? "null"} → zorla reconnect`
      );
      this.forceReconnect(`Inconsistent socket state on resume: ${reason}`);
      return;
    }
    this.kickHeartbeat();
  }

  /** kaynak-referans kickHeartbeat 1-1: planlı tick'i iptal edip HEMEN heartbeat at. */
  private kickHeartbeat(): void {
    if (!this.heartbeatIntervalMs) return;
    if (this.heartbeatTimeoutId != null) {
      workerTimers.clearTimeout(this.heartbeatTimeoutId);
      this.heartbeatTimeoutId = null;
    }
    this.handleHeartbeatTick();
  }

  /**
   * kaynak-referans forceReconnect 1-1: zombi soketi close-handshake beklemeden
   * söker (timer temizliği + teardown + immediate reconnect; backoff sıfır).
   * Session KORUNUR → resume-window içindeyse RESUME ile dönülür.
   */
  private forceReconnect(reason: string): void {
    console.warn(`[WS9100] zombi soket sökülüyor: ${reason}`);
    this.clearHelloTimeout();
    this.clearResumeTimeout();
    if (this.reconnectTimer != null) {
      workerTimers.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.invalidSessionTimeoutId != null) {
      workerTimers.clearTimeout(this.invalidSessionTimeoutId);
      this.invalidSessionTimeoutId = null;
    }
    this.stopHeartbeat();
    // teardown (kaynak-referans teardownSocket paritesi): event'ler stale-guard'la
    // zaten yok sayılır; ws hemen null'lanır ki yeni connect eski soketi görmesin.
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      try {
        if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
          ws.close(1000, "Disposing stale socket");
        }
      } catch {
        /* yut — soket zaten ölü olabilir */
      }
    }
    this.isReady = false;
    this.cb.onClose?.();
    this.closedByUser = false;
    this.shouldReconnectImmediately = true;
    this.backoff.reset();
    this.connectionState = GatewayState.Reconnecting;
    this.scheduleReconnect();
  }

  // ── Outbound op-eşleme (kaynak-referans GatewaySocket.ts 1-1) ──────────────────
  // Hepsi READY/RESUMED sonrası (isReady) çağrılabilir; erken çağrı sessiz
  // düşer (kaynak-referansın `if (!this.isConnected()) return` guard'ı ile aynı).

  /**
   * op3 PRESENCE_UPDATE — :9000 `presence:update` eşleniği. kaynak-referans 1-1: status
   * zorunlu; afk/mobile/custom_status yalnız verildiğinde d'ye eklenir (Discord
   * kısmi-presence semantiği — eksik alan "değişmedi" demek).
   */
  updatePresence(status: string, afk?: boolean, mobile?: boolean, customStatus?: any): void {
    if (!this.isReady) return;
    this.send({
      op: Op.PRESENCE_UPDATE,
      d: {
        status,
        ...(afk !== undefined && { afk }),
        ...(mobile !== undefined && { mobile }),
        ...(customStatus !== undefined && { custom_status: customStatus }),
      },
    });
  }

  /**
   * op4 VOICE_STATE_UPDATE — :9000 `voice:join`/`voice:leave`/mute eşleniği.
   * Kanala katılma channel_id=<id>, ayrılma channel_id=null; guild_id DM voice'ta
   * null. kaynak-referans updateVoiceState 1-1 (minimal alan seti — is_mobile/geo yok).
   */
  updateVoiceState(params: GatewayV2VoiceStateParams): boolean {
    if (!this.isReady) return false;
    return this.send({
      op: Op.VOICE_STATE_UPDATE,
      d: {
        guild_id: params.guild_id ?? null,
        channel_id: params.channel_id ?? null,
        self_mute: params.self_mute,
        self_deaf: params.self_deaf,
        // Opsiyonel kaynak-referans-alanları (yalnız verilmişse — additive, eski
        // çağıranların wire şekli DEĞİŞMEZ).
        ...(params.self_video !== undefined ? { self_video: params.self_video } : {}),
        ...(params.self_stream !== undefined ? { self_stream: params.self_stream } : {}),
        ...(params.viewer_stream_keys !== undefined ? { viewer_stream_keys: params.viewer_stream_keys } : {}),
        ...(params.connection_id !== undefined ? { connection_id: params.connection_id } : {}),
      },
    });
  }

  /**
   * op8 REQUEST_GUILD_MEMBERS — üye-listesi/arama isteği. kaynak-referans
   * requestGuildMembers 1-1: guild_ids öncelikli, yoksa guild_id; query/limit/
   * user_ids/presences/nonce yalnız verildiğinde eklenir. Hedef guild yoksa no-op.
   */
  requestGuildMembers(params: {
    guildId?: string;
    guildIds?: string[];
    query?: string;
    limit?: number;
    userIds?: string[];
    presences?: boolean;
    nonce?: string;
  }): void {
    if (!this.isReady) return;
    const guildIds = params.guildIds ? [...new Set(params.guildIds.filter((g) => g.length > 0))] : [];
    const guildPayload: { guild_id?: string; guild_ids?: string[] } = {};
    if (guildIds.length > 0) {
      guildPayload.guild_ids = guildIds;
    } else if (params.guildId) {
      guildPayload.guild_id = params.guildId;
    }
    if (!guildPayload.guild_id && !guildPayload.guild_ids) return;
    this.send({
      op: Op.REQUEST_GUILD_MEMBERS,
      d: {
        ...guildPayload,
        ...(params.query !== undefined && { query: params.query }),
        ...(params.limit !== undefined && { limit: params.limit }),
        ...(params.userIds !== undefined && { user_ids: [...new Set(params.userIds)] }),
        ...(params.presences !== undefined && { presences: params.presences }),
        ...(params.nonce !== undefined && { nonce: params.nonce }),
      },
    });
  }

  /**
   * op14 LAZY_REQUEST — kanal-scope üye-listesi penceresi + typing/presence
   * abonelikleri (Discord lazy guild). kaynak-referans updateGuildSubscriptions 1-1;
   * d.subscriptions guild_id → {active, member_list_channels, typing, members,
   * sync} sözlüğü. Sürekli-scroll member-list bunu kullanır (op8 tek-atış arama).
   */
  updateGuildSubscriptions(
    subscriptions: Record<
      string,
      {
        active?: boolean;
        member_list_channels?: Record<string, Array<[number, number]>>;
        typing?: boolean;
        members?: string[];
        sync?: boolean;
      }
    >
  ): void {
    if (!this.isReady) return;
    this.send({ op: Op.LAZY_REQUEST, d: { subscriptions } });
  }

  /**
   * op15 REQUEST_GUILD_COUNTS — guild üye/online sayımı. kaynak-referans
   * requestGuildCounts 1-1: guild_ids dedupe+boş-filtre; boşsa no-op.
   * Erlang tarafı guild_request_counts.erl (gateway_handler_dispatch
   * handle_request_guild_counts) tarafından işlenir.
   */
  requestGuildCounts(guildIds: string[]): void {
    if (!this.isReady) return;
    const uniqueGuildIds = [...new Set(guildIds.filter((id) => id.length > 0))];
    if (uniqueGuildIds.length === 0) return;
    this.send({ op: Op.REQUEST_GUILD_COUNTS, d: { guild_ids: uniqueGuildIds } });
  }

  /**
   * op16 REQUEST_CHANNEL_MEMBER_COUNTS — kanal-bazlı üye sayımı. kaynak-referans
   * requestChannelMemberCounts 1-1: guildId zorunlu, channelIds dedupe+boş-
   * filtre; nonce yalnız verildiğinde eklenir. Erlang tarafı
   * guild_request_channel_member_counts.erl tarafından işlenir.
   */
  requestChannelMemberCounts(params: { guildId: string; channelIds: string[]; nonce?: string }): void {
    if (!this.isReady) return;
    if (params.guildId.length === 0) return;
    const uniqueChannelIds = [...new Set(params.channelIds.filter((id) => id.length > 0))];
    if (uniqueChannelIds.length === 0) return;
    this.send({
      op: Op.REQUEST_CHANNEL_MEMBER_COUNTS,
      d: {
        guild_id: params.guildId,
        channel_ids: uniqueChannelIds,
        ...(params.nonce !== undefined && { nonce: params.nonce }),
      },
    });
  }

  // ── Wire ──

  private handleFrame(raw: string): void {
    let msg: WirePayload;
    try {
      msg = JSON.parse(raw);
    } catch (e) {
      console.warn("[WS9100] parse error:", e);
      return;
    }
    this.lastGatewayMessageAt = Date.now();
    // referans-parite: sequence yalnız DISPATCH'te VE yalnız artarken güncellenir
    // (monotonluk garantisi — geç gelen/yeniden-sıralı frame'ler seq'i geri almaz).
    if (msg.op === Op.DISPATCH && typeof msg.s === "number" && msg.s > this.lastSeq) {
      this.lastSeq = msg.s;
    }

    switch (msg.op) {
      case Op.HELLO:
        this.onHello(msg.d);
        break;
      case Op.HEARTBEAT:
        // Sunucu heartbeat istedi → hemen gönder (server-requested, ack-timeout
        // yeniden kurulur; kaynak-referans sendHeartbeat(true) paritesi).
        this.sendHeartbeat(true);
        break;
      case Op.HEARTBEAT_ACK:
        this.handleHeartbeatAck();
        break;
      case Op.DISPATCH:
        this.onDispatch(msg);
        break;
      case Op.INVALID_SESSION:
        this.handleInvalidSession(msg.d);
        break;
      case Op.RECONNECT:
        console.log("[WS9100] server RECONNECT istedi");
        // kaynak-referans: shouldReconnectImmediately=true + resumable disconnect (session
        // KORUNUR — sunucu resume bekliyor, sıfırdan IDENTIFY'a gerek yok).
        this.shouldReconnectImmediately = true;
        this.triggerReconnect(CloseCode.UNKNOWN_ERROR, "Server requested reconnect");
        break;
      case Op.GATEWAY_ERROR:
        console.warn("[WS9100] gateway error:", msg.d);
        this.cb.onGatewayError?.(msg.d);
        break;
      default:
        break;
    }
  }

  private onHello(d: any): void {
    this.clearHelloTimeout();
    if (this.invalidSessionTimeoutId != null) {
      workerTimers.clearTimeout(this.invalidSessionTimeoutId);
      this.invalidSessionTimeoutId = null;
    }
    const interval = Number(d?.heartbeat_interval) || 41250;
    this.startHeartbeat(interval);
    // Resume edilebilir session varsa dene (kaynak-referans canResumeSession: session +
    // resume-window(180s) tazeliği); yoksa sıfırdan IDENTIFY.
    if (this.canResumeSession()) {
      this.sendResume();
    } else {
      this.sendIdentify();
    }
  }

  private onDispatch(msg: WirePayload): void {
    const t = msg.t;
    if (!t) return;
    if (t === "READY") {
      this.sessionId = (msg.d as any)?.session_id ?? this.sessionId;
      // kaynak-referans READY bloğu 1-1: heartbeat geçmişi sıfırlanır (eski
      // bağlantının sent/ack zaman damgaları resume-window hesabına sızmasın),
      // backoff sıfırlanır, durum Connected olur.
      this.resetHeartbeatHistory();
      this.backoff.reset();
      this.isReady = true;
      this.connectionState = GatewayState.Connected;
      console.log("[WS9100] READY, session=", this.sessionId);
      this.cb.onReady?.(msg.d);
    } else if (t === "RESUMED") {
      // RESUME başarılı: sunucu kaçırılan event'leri replay etti. kaynak-referans
      // paritesi — backoff sıfırla + resume-timeout temizle + "connected"
      // yeniden yay. onReady köprüsü ws-client'ta emitLocal("connected")
      // tetikler → UI yeniden "bağlı" olur.
      this.clearResumeTimeout();
      this.resetHeartbeatHistory();
      this.backoff.reset();
      this.isReady = true;
      this.connectionState = GatewayState.Connected;
      console.log("[WS9100] RESUMED, session=", this.sessionId);
      this.cb.onReady?.(msg.d);
    }
    // GATEWAY-EVENT-CUTOVER (2026-07-16): eski GatewayEventTranslation.ts
    // (UPPERCASE→legacy-ad çeviri tablosu) SÖKÜLDÜ — kaynak-referans deseni: handler'lar
    // doğrudan wire'daki UPPER_SNAKE `t` adını tüketir, ara-çeviri YOK.
    // READY/RESUMED de aynı yoldan köprülenir: READY'nin FE case'i var
    // (handleReady); RESUMED'in FE case'i YOK → identity passthrough zararsız
    // düşer.
    // TEK-DEPO GÖÇÜ S0.7 (21 Eyl 2026): TYPING_START artık `typing:{kanal}`
    // alt-konusuna ÇEVRİLMEZ (eski `resolveDispatchTopic` söküldü). Çeviri
    // yüzünden olay `dispatchStaticEvent`e ham adıyla hiç ulaşmıyor, portlanmış
    // `TYPING_START` handler'ı (ReferenceRouter) çalışamıyordu. Ad/görsel
    // zenginleştirmesi eski depoya özgüydü → handlers/typing/TypingStart.ts'e taşındı.
    this.cb.onDispatch(t, msg.d);
  }

  private sendIdentify(): void {
    console.log("[WS9100] IDENTIFY gönderiliyor");
    this.send({
      op: Op.IDENTIFY,
      d: {
        token: this.opts.token,
        // kaynak-referans GatewayConnection.ts:262-263 paritesi — reaction-debounce
        // bit'i HER ZAMAN açık: oturum, reaction seli yerine 650ms'de bir
        // MESSAGE_REACTION_ADD_MANY alır (handler: MessageReactionAddMany.ts).
        flags: IdentifyFlags.DEBOUNCE_MESSAGE_REACTIONS,
        // Minimal properties — Erlang IDENTIFY öncelikle token doğrular.
        properties: {
          os: "web",
          browser: "tebhos",
          device: "tebhos_app",
        },
        presence: {
          status: this.opts.initialStatus,
          afk: false,
        },
      },
    });
  }

  private sendResume(): void {
    if (!this.sessionId) {
      console.warn("[WS9100] RESUME için session yok, IDENTIFY'a düşülüyor");
      this.sendIdentify();
      return;
    }
    console.log("[WS9100] RESUME gönderiliyor, session=", this.sessionId);
    this.send({
      op: Op.RESUME,
      d: {
        token: this.opts.token,
        session_id: this.sessionId,
        seq: this.lastSeq,
      },
    });
    this.startResumeTimeout();
  }

  /**
   * kaynak-referans canResumeSession 1-1: YALNIZ session varlığına + son gateway
   * aktivitesinin resume-window (180s) içinde olmasına bakar. (Eski TEBHOS
   * sapması: lastSeq==null'da resume reddediliyordu — kaynak-referansta seq şartı
   * YOK; seq 0'dan başlar.) Aktivite hiç kaydedilmemişse izin ver.
   */
  /**
   * Geçerli gateway oturum kimliği (READY/RESUMED ile gelir). REST çağrıları bunu
   * `?session_id=` olarak gönderir → sunucu, olayı üreten oturumu yayından hariç
   * tutabilir (kaynak-referans GatewayConnection.sessionId paritesi).
   */
  getSessionId(): string | null {
    return this.sessionId;
  }

  /**
   * kaynak-referans GatewaySocket.setToken 1-1: yalnız saklanan token değişir;
   * bir SONRAKİ IDENTIFY/RESUME onu kullanır (açık bağlantı yeniden kurulmaz).
   */
  setToken(token: string): void {
    this.opts.token = token;
  }

  private canResumeSession(): boolean {
    if (!this.sessionId) return false;
    const lastActivityAt = this.lastGatewayActivityAt();
    if (lastActivityAt == null) return true;
    return Date.now() - lastActivityAt <= TIMEOUTS.ResumeWindow;
  }

  /**
   * kaynak-referans lastGatewayActivityAt 1-1: ham mesaj trafiği + heartbeat
   * aktivitesi (SENT ve ACK'in en yenisi — kaynak-referans lastHeartbeatActivityAt).
   * Eski TEBHOS sapması: yalnız ACK sayılıyordu; ack'i henüz gelmemiş taze bir
   * heartbeat gönderimi resume-window'u yanlışlıkla düşürebiliyordu.
   */
  private lastGatewayActivityAt(): number | null {
    const lastHeartbeatAt = this.lastHeartbeatActivityAt();
    if (this.lastGatewayMessageAt == null) return lastHeartbeatAt;
    if (lastHeartbeatAt == null) return this.lastGatewayMessageAt;
    return Math.max(this.lastGatewayMessageAt, lastHeartbeatAt);
  }

  /** kaynak-referans lastHeartbeatActivityAt 1-1. */
  private lastHeartbeatActivityAt(): number | null {
    if (this.lastHeartbeatAckAt == null) return this.lastHeartbeatSentAt;
    if (this.lastHeartbeatSentAt == null) return this.lastHeartbeatAckAt;
    return Math.max(this.lastHeartbeatAckAt, this.lastHeartbeatSentAt);
  }

  /** kaynak-referans resetHeartbeatHistory 1-1 — READY/RESUMED'da çağrılır. */
  private resetHeartbeatHistory(): void {
    this.lastHeartbeatAckAt = null;
    this.lastHeartbeatSentAt = null;
  }

  private handleInvalidSession(d: any): void {
    const isResumable = d === true;
    console.warn(`[WS9100] INVALID_SESSION (resumable=${isResumable})`);
    if (this.invalidSessionTimeoutId != null) {
      workerTimers.clearTimeout(this.invalidSessionTimeoutId);
      this.invalidSessionTimeoutId = null;
    }
    // referans-parite: Discord protokolü invalid_session sonrası HEMEN
    // reconnect'i yasaklar — 2.5-3.5sn jitter'lı bekleme (bağlantı fırtınasını
    // önlemek için). Yalnız resumable=false ise session temizlenir.
    const delay = 2500 + Math.random() * 1000;
    this.invalidSessionTimeoutId = workerTimers.setTimeout(() => {
      this.invalidSessionTimeoutId = null;
      if (!isResumable) {
        this.clearSession();
      }
      this.shouldReconnectImmediately = true;
      this.triggerReconnect(CloseCode.UNKNOWN_ERROR, `Invalid session (resumable=${isResumable})`);
    }, delay);
  }

  // ── Heartbeat (kaynak-referans GatewaySocket.ts heartbeat bloğu 1-1) ──

  private startHeartbeat(intervalMs: number): void {
    this.stopHeartbeat();
    this.heartbeatIntervalMs = intervalMs;
    this.scheduleHeartbeat(this.computeNextHeartbeatDelay());
  }

  /** İlk/sıradaki heartbeat gecikmesi: ~%80 interval + ≤%5 jitter (kaynak-referans 1-1). */
  private computeNextHeartbeatDelay(): number {
    if (!this.heartbeatIntervalMs || this.heartbeatIntervalMs <= 0) return 1000;
    const base = Math.max(1000, Math.floor(this.heartbeatIntervalMs * 0.8));
    const jitter = Math.min(1500, Math.floor(this.heartbeatIntervalMs * 0.05));
    return base + Math.floor(Math.random() * (jitter + 1));
  }

  private scheduleHeartbeat(delayMs?: number): void {
    if (!this.heartbeatIntervalMs) return;
    const delay = delayMs ?? this.computeNextHeartbeatDelay();
    if (this.heartbeatTimeoutId != null) workerTimers.clearTimeout(this.heartbeatTimeoutId);
    // WS-CHURN FIX: worker-timer — arka plan sekmesinde de ~33sn kadansı korunur.
    this.heartbeatTimeoutId = workerTimers.setTimeout(() => this.handleHeartbeatTick(), delay);
  }

  private handleHeartbeatTick(): void {
    this.heartbeatTimeoutId = null;
    this.sendHeartbeat(false);
    if (this.heartbeatIntervalMs) this.scheduleHeartbeat();
  }

  /** Ack beklerken yeniden denemeden önceki eşik (kaynak-referans heartbeatSkipThreshold 1-1). */
  private heartbeatSkipThreshold(): number {
    if (!this.heartbeatIntervalMs || this.heartbeatIntervalMs <= 0) return TIMEOUTS.HeartbeatAck;
    const derived = Math.floor(this.heartbeatIntervalMs * 0.75);
    return Math.max(500, Math.min(TIMEOUTS.HeartbeatAck, derived));
  }

  private sendHeartbeat(serverRequested: boolean): void {
    if (this.awaitingAck && !serverRequested) {
      const now = Date.now();
      const elapsed = this.lastHeartbeatSentAt ? now - this.lastHeartbeatSentAt : 0;
      const skipThreshold = this.heartbeatSkipThreshold();
      if (elapsed < skipThreshold) {
        this.scheduleHeartbeat(Math.max(500, skipThreshold - elapsed));
        return;
      }
      if (elapsed < TIMEOUTS.HeartbeatAck) {
        this.scheduleHeartbeat(Math.max(500, TIMEOUTS.HeartbeatAck - elapsed));
        return;
      }
      console.warn("[WS9100] heartbeat ACK zaman aşımı → reconnect");
      this.handleHeartbeatFailure();
      return;
    }
    const ok = this.send({ op: Op.HEARTBEAT, d: this.lastSeq });
    if (!ok) {
      console.warn("[WS9100] heartbeat gönderilemedi → reconnect");
      this.handleHeartbeatFailure();
      return;
    }
    this.awaitingAck = true;
    this.lastHeartbeatSentAt = Date.now();
    if (serverRequested && this.heartbeatAckTimeoutId != null) {
      workerTimers.clearTimeout(this.heartbeatAckTimeoutId);
    }
    this.startHeartbeatAckTimeout();
    if (serverRequested && this.heartbeatIntervalMs) this.scheduleHeartbeat();
  }

  /**
   * Ack için 15sn sabit zaman aşımı (kaynak-referans TIMEOUTS.HeartbeatAck) — heartbeat
   * ARALIĞINDAN BAĞIMSIZ. Eski TEBHOS implementasyonunda bu yoktu (yalnız bir
   * sonraki interval-tick'te awaitingAck kontrolü yapılıyordu); büyük
   * interval'larda (41.25sn) zombie bağlantı çok geç fark edilirdi.
   */
  private startHeartbeatAckTimeout(): void {
    this.heartbeatAckTimeoutId = workerTimers.setTimeout(() => {
      if (!this.awaitingAck) return;
      console.warn("[WS9100] heartbeat ACK zaman aşımı (bağımsız timer) → reconnect");
      this.handleHeartbeatFailure();
    }, TIMEOUTS.HeartbeatAck);
  }

  private handleHeartbeatAck(): void {
    this.awaitingAck = false;
    this.lastHeartbeatAckAt = Date.now();
    if (this.heartbeatAckTimeoutId != null) {
      workerTimers.clearTimeout(this.heartbeatAckTimeoutId);
      this.heartbeatAckTimeoutId = null;
    }
  }

  private handleHeartbeatFailure(): void {
    this.shouldReconnectImmediately = true;
    this.triggerReconnect(CloseCode.UNKNOWN_ERROR, "Heartbeat ACK timeout");
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimeoutId != null) {
      workerTimers.clearTimeout(this.heartbeatTimeoutId);
      this.heartbeatTimeoutId = null;
    }
    if (this.heartbeatAckTimeoutId != null) {
      workerTimers.clearTimeout(this.heartbeatAckTimeoutId);
      this.heartbeatAckTimeoutId = null;
    }
    this.awaitingAck = false;
    this.heartbeatIntervalMs = 0;
  }

  // ── HELLO / RESUME zaman aşımları (kaynak-referans 1-1, eski TEBHOS'ta YOKTU) ──

  /** HELLO 20sn içinde gelmezse bağlantı zombie sayılır (kaynak-referans TIMEOUTS.Hello). */
  private startHelloTimeout(): void {
    this.clearHelloTimeout();
    this.helloTimeoutId = workerTimers.setTimeout(() => {
      this.helloTimeoutId = null;
      console.warn("[WS9100] HELLO zaman aşımı (20sn) → reconnect");
      this.triggerReconnect(CloseCode.UNKNOWN_ERROR, "Hello timeout");
    }, TIMEOUTS.Hello);
  }

  private clearHelloTimeout(): void {
    if (this.helloTimeoutId != null) {
      workerTimers.clearTimeout(this.helloTimeoutId);
      this.helloTimeoutId = null;
    }
  }

  /**
   * RESUME gönderildikten sonra RESUMED 15sn içinde gelmezse resume başarısız
   * sayılır — session temizlenir, sıfırdan bağlanılır (kaynak-referans TIMEOUTS.ResumeResponse).
   * kaynak-referanstaki gibi bu yol İMMEDIATE değil (backoff'a tabi) — resume'un
   * tekrar tekrar hemen başarısız olup sunucuyu yormaması için.
   */
  private startResumeTimeout(): void {
    this.clearResumeTimeout();
    this.resumeTimeoutId = workerTimers.setTimeout(() => {
      this.resumeTimeoutId = null;
      console.warn("[WS9100] RESUMED zaman aşımı (15sn) → session temizlenip yeniden bağlanılıyor");
      this.clearSession();
      this.triggerReconnect(CloseCode.UNKNOWN_ERROR, "Resume response timeout");
    }, TIMEOUTS.ResumeResponse);
  }

  private clearResumeTimeout(): void {
    if (this.resumeTimeoutId != null) {
      workerTimers.clearTimeout(this.resumeTimeoutId);
      this.resumeTimeoutId = null;
    }
  }

  private clearAllTimers(): void {
    this.clearHelloTimeout();
    this.clearResumeTimeout();
    if (this.invalidSessionTimeoutId != null) {
      workerTimers.clearTimeout(this.invalidSessionTimeoutId);
      this.invalidSessionTimeoutId = null;
    }
    if (this.reconnectTimer != null) {
      workerTimers.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  // ── Close / reconnect (kaynak-referans handleSocketClose + scheduleReconnect 1-1) ──

  private handleClose(code: number, reason: string): void {
    // kaynak-referans handleSocketClose 1-1: HER kapanış close-code + reason ile
    // loglanır (`WebSocket closed [code] reason`). Eski TEBHOS sapması: kod
    // yalnız session-temizleme dalında görünüyordu — prod'da "kim, neden
    // kapattı?" sorusu konsoldan yanıtlanamıyordu (bu teşhisin kendisi de
    // bu log eksiği yüzünden uzadı).
    console.warn(`[WS9100] WebSocket closed [${code}] ${reason || ""}`);
    this.isReady = false;
    this.clearHelloTimeout();
    const resumeWasPending = this.resumeTimeoutId != null;
    this.clearResumeTimeout();
    if (this.invalidSessionTimeoutId != null) {
      workerTimers.clearTimeout(this.invalidSessionTimeoutId);
      this.invalidSessionTimeoutId = null;
    }
    this.stopHeartbeat();
    this.cb.onClose?.();

    if (code === CloseCode.AUTHENTICATION_FAILED) {
      // kaynak-referans handleAuthFailure paritesi: kalıcı hata, otomatik retry YOK.
      console.error("[WS9100] AUTHENTICATION_FAILED — reconnect denenmeyecek");
      this.closedByUser = true;
      this.connectionState = GatewayState.Disconnected;
      this.clearSession();
      this.cb.onAuthFailure?.();
      return;
    }

    // kaynak-referans 1-1: seq geçersiz / oturum zaman aşımı / resume yarım kaldıysa
    // session temizlenir (bir sonraki bağlantı sıfırdan IDENTIFY eder).
    if (code === CloseCode.INVALID_SEQ || code === CloseCode.SESSION_TIMEOUT || resumeWasPending) {
      console.log(`[WS9100] resume başarısız/oturum geçersiz [${code}] ${reason} → session temizleniyor`);
      this.clearSession();
    }

    if (!this.closedByUser) {
      // kaynak-referans handleConnectionFailure 1-1: Reconnecting durumuna geç.
      this.connectionState = GatewayState.Reconnecting;
      this.scheduleReconnect();
    } else {
      this.connectionState = GatewayState.Disconnected;
    }
  }

  /**
   * Kontrollü (istemci-kaynaklı) reconnect tetikleyici — kaynak-referans
   * `disconnect(code, reason, resumable=true)` 1-1: timer'lar temizlenir,
   * heartbeat durdurulur, soket kapatılır ve reconnect close-event
   * BEKLENMEDEN hemen planlanır (kaynak-referans disconnect resumable-dalı da
   * `updateState(Reconnecting) + scheduleReconnect()` yapar; handleClose'un
   * sonraki çağrısı idempotent no-op'a düşer).
   */
  private triggerReconnect(code: number, reason: string): void {
    // WS-CHURN FIX (2026-08-11) — kaynak-referans disconnect() teardown'u
    // TAMAMLANDI: eski bağlantının hello/resume/invalid-session zamanlayıcıları
    // ve heartbeat'i, yeni deneme planlanmadan ÖNCE susturulur. Önceden bunlar
    // temizlenmiyordu; immediate (0ms) reconnect penceresinde bayat bir
    // heartbeat tick'i CONNECTING'deki YENİ soketi "heartbeat gönderilemedi"
    // diye kapatıp gereksiz ekstra churn üretebiliyordu. (resume-timeout'un
    // burada temizlenmesi kaynak-referansla aynı: session'ı temizleme kararını
    // yalnız startResumeTimeout'un kendi yolu ve handleClose verir.)
    this.clearHelloTimeout();
    this.clearResumeTimeout();
    if (this.invalidSessionTimeoutId != null) {
      workerTimers.clearTimeout(this.invalidSessionTimeoutId);
      this.invalidSessionTimeoutId = null;
    }
    this.stopHeartbeat();
    const ws = this.ws;
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
      try {
        ws.close(code, reason);
      } catch {
        /* aşağıda yine de planlanır */
      }
    }
    if (!this.closedByUser) {
      this.connectionState = GatewayState.Reconnecting;
      this.scheduleReconnect();
    }
  }

  /**
   * kaynak-referans scheduleReconnect 1-1: zaten planlıysa no-op (idempotent — hem
   * proaktif hem reaktif çağrı güvenli); `allowImmediate` kapalıysa immediate
   * bayrağı yok sayılır (kaynak-referans reconnectAfterResume backoff-koruma yolu);
   * gecikme nextReconnectDelay'den gelir. ESKİ TEBHOS'taki 10-deneme tavanı
   * KALDIRILDI (kaynak-referans sınırsız dener).
   */
  private scheduleReconnect(options: { allowImmediate?: boolean } = {}): void {
    if (this.closedByUser) return;
    if (this.reconnectTimer != null) return;
    const allowImmediate = options.allowImmediate ?? true;
    const immediate = allowImmediate && this.shouldReconnectImmediately;
    this.shouldReconnectImmediately = false;
    const delay = immediate ? 0 : this.nextReconnectDelay();
    console.log(`[WS9100] reconnect planlandı (${delay}ms${immediate ? ", immediate" : ""})`);
    // WS-CHURN FIX: worker-timer — arka plandaki sekmede reconnect dakikalarca
    // ertelenmesin; 60sn'lik gateway resume-TTL'i dolmadan RESUME denenebilsin.
    this.reconnectTimer = workerTimers.setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.canResumeSession()) this.clearSession();
      this.connect();
    }, delay);
  }

  /**
   * kaynak-referans nextReconnectDelay 1-1: son reconnect'ten bu yana MinReconnect
   * geçmediyse minimum gecikme dayatılır (immediate-fırtına freni), aksi halde
   * jitter'lı exponential backoff.
   */
  private nextReconnectDelay(): number {
    const now = Date.now();
    const elapsed = now - this.lastReconnectAt;
    if (elapsed < TIMEOUTS.MinReconnect) {
      return TIMEOUTS.MinReconnect;
    }
    this.lastReconnectAt = now;
    return this.backoff.next();
  }

  private clearSession(): void {
    this.sessionId = null;
    this.lastSeq = 0;
    this.lastGatewayMessageAt = null;
    this.resetHeartbeatHistory();
  }

  /**
   * kaynak-referans sendPayload 1-1: sıkıştırma açıksa (canCompress()) giden JSON'u
   * WASM CCtx ile flush edip binary gönderir, aksi halde düz text. Outbound
   * sıkıştırma hatası da recoverable-fallback yoluna düşer (encoder da aynı
   * WASM modülünü paylaştığından decoder ile aynı arıza sınıfı).
   */
  private send(payload: WirePayload): boolean {
    if (this.ws?.readyState !== WebSocket.OPEN) {
      console.warn("[WS9100] soket açık değilken gönderim denendi");
      return false;
    }
    // kaynak-referans sendPayload 1-1: TÜM gönderim yolu try/catch'te — `ws.send`
    // fırlatırsa (ör. kapanma yarışı InvalidStateError) istisna yutulup false
    // dönülür; heartbeat yolu bunu handleHeartbeatFailure'a çevirir. Eski
    // TEBHOS sapması: düz-metin `ws.send` korumasızdı, timer içinde fırlarsa
    // heartbeat sessizce kaybolur, gönderilmemiş sayılmazdı.
    try {
      const data = JSON.stringify(payload);
      const codec = this.payloadDecompressor;
      if (codec?.canCompress()) {
        let compressed: Uint8Array;
        try {
          compressed = codec.compress(data);
        } catch (e) {
          const error = e instanceof Error ? e : new Error(String(e));
          console.error("[WS9100] outbound compression hatası:", error);
          this.handleRecoverableCompressionDecodeError(error);
          return false;
        }
        // kaynak-referans 1-1: WASM belleğine bakan view'i kopyalayarak gönder
        // (`new Uint8Array(compressed)`) — WASM belleği büyüyüp buffer detach
        // olursa view sıfır-uzunluğa düşer; kopya bu yarışı kapatır.
        this.ws.send(new Uint8Array(compressed));
        return true;
      }
      this.ws.send(data);
      return true;
    } catch (e) {
      console.error("[WS9100] gateway payload gönderilemedi:", e);
      return false;
    }
  }
}
